import { OnDestroy, Injectable, NgZone, effect, inject } from '@angular/core';
import { Observable, BehaviorSubject, from, throwError, of } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import {
  ServiceProvider,
  ServiceProviderType,
  isClaimedProfile,
  masterFieldsTouchedBy,
} from '@shared/definitions/service-provider';
import { isPermissionDeniedError } from '@core/services/auth/auth-transition-error';
import {
  Firestore} from '@angular/fire/firestore';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  getDoc,
  Timestamp
} from '@angular/fire/firestore';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { toDate } from '@shared/utils/to-date';

/**
 * Shapes the document sent to `updateDoc` when a provider is edited.
 *
 * Extracted from `updateServiceProvider` purely as a testability seam: the write
 * itself goes through a module-level `updateDoc` import that Karma cannot stub,
 * so the only way to assert this contract was to make it a function. It is the
 * contract that P-03 broke — an edit that carried `performanceMetrics` wiped a
 * provider's whole history — so it is worth being able to pin.
 *
 * Two responsibilities, no more:
 *  - drop the identity fields a caller must never be able to rewrite
 *  - flatten `bankAccount` so no nested `undefined` reaches Firestore, which
 *    rejects it (the frontend SDK has no `ignoreUndefinedProperties`)
 *
 * Deliberately NOT a whitelist: callers decide which fields they own. The add
 * form sends only what its form holds, which is what keeps ratings and metrics
 * intact — see `buildUpdatePayload` in add-contractor.component.ts.
 */
/**
 * Raised when an owner tries to rewrite a field a claimed provider owns (#218).
 *
 * A distinct type, not a generic Error: the caller has to be able to tell "the
 * write is refused because this profile is not yours to edit" from "the write
 * failed" — the first is a rule the UI should explain, the second is a retry.
 */
export class ProviderProfileLockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProviderProfileLockedError';
  }
}

export type ProviderUpdatePayload = Omit<Partial<ServiceProvider>, 'updatedAt'> & {
  // `ServiceProvider` declares `updatedAt: Date`, but what goes on the wire is a
  // Firestore Timestamp. The mismatch predates this extraction and belongs to
  // the frozen data-model debt — `Omit` states what is actually written instead
  // of intersecting the two into an impossible `Date & Timestamp`.
  updatedAt: Timestamp;
};

export function buildProviderUpdatePayload(
  updates: Partial<ServiceProvider>
): ProviderUpdatePayload {
  const updateData: ProviderUpdatePayload = {
    ...updates,
    updatedAt: Timestamp.fromDate(new Date())
  };

  // Identity is assigned once, at creation, and is not the caller's to change.
  delete updateData.providerUID;
  delete updateData.createdAt;

  if (updateData.bankAccount) {
    updateData.bankAccount = {
      iban: updateData.bankAccount.iban || '',
      bic: updateData.bankAccount.bic || '',
      accountHolder: updateData.bankAccount.accountHolder || ''
    };
  }

  // Frontend SDK has no ignoreUndefinedProperties — never send undefined.
  if (updateData.logoUrl === undefined) {
    delete updateData.logoUrl;
  }

  return updateData;
}

/**
 * Shapes one Firestore document into a `ServiceProvider`.
 *
 * Extracted from the `onSnapshot` listener as a testability seam, for the same
 * reason `buildProviderUpdatePayload` was: the mapping had no test because it
 * was unreachable, and that is how #375 shipped. Every date field went through
 * `data['x']?.toDate()`, where `?.` guards `null` and `undefined` and nothing
 * else — so a value that is PRESENT but carries no `toDate` method threw
 * `toDate is not a function`, inside a `.map()`, which failed the whole list.
 *
 * That is not a hypothetical shape. One of the two providers on `proporio-dev`
 * stores `createdAt` as `{ _seconds, _nanoseconds }` — a Timestamp that went
 * through JSON and came back without its methods. One document out of two was
 * enough to blank the screen.
 *
 * `toDate` (shared/utils/to-date.ts) already folded every one of these shapes
 * into a `Date`, and already had a spec. It simply was not called here.
 *
 * @param id The document id, which becomes `providerUID`.
 * @param data The raw document data.
 * @return The mapped provider.
 */
export function mapServiceProviderDoc(
  id: string,
  data: Record<string, unknown>,
): ServiceProvider {
  const d = data as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

  return {
    providerUID: id,
    name: d['name'] || '',
    type: d['type'] || ServiceProviderType.OTHER,
    contactInfo: {
      phone: d['contactInfo']?.phone || '',
      email: d['contactInfo']?.email || '',
      address: d['contactInfo']?.address || '',
      ...(d['contactInfo']?.contactPerson ? {
        contactPerson: {
          firstName: d['contactInfo'].contactPerson.firstName || '',
          lastName: d['contactInfo'].contactPerson.lastName || ''
        }
      } : {})
    },
    rating: d['rating'] || 0,
    isActive: d['isActive'] !== undefined ? d['isActive'] : true,
    /**
     * Le consentement WhatsApp et son veto (#377).
     *
     * Ce mapper ENUMERE les champs — il ne recopie pas le document. Les avoir
     * ajoutes au modele et au chemin d'ecriture ne suffisait donc pas : la
     * fiche revenait sans eux, la bascule s'affichait decochee sur un
     * prestataire consentant, et l'avertissement de STOP ne pouvait jamais
     * apparaitre. Trouve par le pas C7 de la recette 380.
     *
     * `=== true` des deux cotes : un champ absent vaut « pas de consentement »
     * et « pas de veto », ce qui est le bon defaut pour l'un comme pour
     * l'autre.
     */
    whatsappOptIn: d['whatsappOptIn'] === true,
    whatsappOptedOut: d['whatsappOptedOut'] === true,
    specialties: d['specialties'] || [],
    notes: d['notes'] || '',
    createdAt: toDate(d['createdAt']) ?? new Date(),
    updatedAt: toDate(d['updatedAt']) ?? new Date(),

    // Enhanced fields
    propertyPreferences: d['propertyPreferences'] ? {
      preferredPropertyTypes: d['propertyPreferences'].preferredPropertyTypes || [],
      maximumProperties: d['propertyPreferences'].maximumProperties,
      preferredLocations: d['propertyPreferences'].preferredLocations || [],
      excludedLocations: d['propertyPreferences'].excludedLocations || [],
      minimumJobValue: d['propertyPreferences'].minimumJobValue,
      notes: d['propertyPreferences'].notes
    } : undefined,

    communicationSettings: d['communicationSettings'] ? {
      preferredMethod: d['communicationSettings'].preferredMethod,
      emergencyContact: d['communicationSettings'].emergencyContact,
      responseTimeExpected: d['communicationSettings'].responseTimeExpected,
      workingHours: d['communicationSettings'].workingHours,
      availableDays: d['communicationSettings'].availableDays
    } : undefined,

    performanceMetrics: d['performanceMetrics'] ? {
      completedJobs: d['performanceMetrics'].completedJobs || 0,
      averageRating: d['performanceMetrics'].averageRating || 0,
      onTimeCompletionRate: d['performanceMetrics'].onTimeCompletionRate || 0,
      responseTimeAverage: d['performanceMetrics'].responseTimeAverage || 0,
      customerSatisfactionScore: d['performanceMetrics'].customerSatisfactionScore || 0,
      repeatCustomerRate: d['performanceMetrics'].repeatCustomerRate || 0,
      lastPerformanceUpdate: toDate(d['performanceMetrics'].lastPerformanceUpdate) ?? new Date()
    } : undefined,

    availabilitySchedule: d['availabilitySchedule'] ? {
      availability: d['availabilitySchedule'].availability || [],
      emergencyAvailable: d['availabilitySchedule'].emergencyAvailable || false,
      leadTimeRequired: d['availabilitySchedule'].leadTimeRequired || 0,
      maxJobsPerWeek: d['availabilitySchedule'].maxJobsPerWeek,
      currentlyAvailable: d['availabilitySchedule'].currentlyAvailable !== false,
      nextAvailableDate: toDate(d['availabilitySchedule'].nextAvailableDate) ?? undefined,
      unavailablePeriods: d['availabilitySchedule'].unavailablePeriods?.map(
        (period: { start?: unknown; end?: unknown; reason?: string }) => ({
          start: toDate(period.start) ?? undefined,
          end: toDate(period.end) ?? undefined,
          reason: period.reason
        })) || []
    } : undefined,

    businessInfo: d['businessInfo'] ? {
      companyName: d['businessInfo'].companyName,
      licenseNumber: d['businessInfo'].licenseNumber,
      insuranceDetails: d['businessInfo'].insuranceDetails,
      website: d['businessInfo'].website,
      yearsInBusiness: d['businessInfo'].yearsInBusiness,
      certifications: d['businessInfo'].certifications || []
    } : undefined,

    bankAccount: d['bankAccount'] ? {
      iban: d['bankAccount'].iban || '',
      bic: d['bankAccount'].bic || '',
      accountHolder: d['bankAccount'].accountHolder || ''
    } : undefined,

    ...(d['logoUrl'] ? { logoUrl: d['logoUrl'] as string } : {}),

    // #645 — réclamation, invitation, langue, favori. Le mapper ÉNUMÈRE : ces
    // champs absents d'ici étaient perdus à la lecture, et avec eux le verrou
    // de fiche réclamée et le badge d'invitation côté écran.
    ...(d['claimStatus'] ? { claimStatus: d['claimStatus'] } : {}),
    ...(d['claimedByWorkspaceId'] ? { claimedByWorkspaceId: d['claimedByWorkspaceId'] as string } : {}),
    ...(toDate(d['claimedAt']) ? { claimedAt: toDate(d['claimedAt']) as Date } : {}),
    ...(d['isMasterProfile'] === true ? { isMasterProfile: true } : {}),
    ...(d['invitationId'] ? { invitationId: d['invitationId'] as string } : {}),
    ...(toDate(d['invitationSentAt']) ? { invitationSentAt: toDate(d['invitationSentAt']) as Date } : {}),
    ...(toDate(d['invitationOpenedAt']) ? { invitationOpenedAt: toDate(d['invitationOpenedAt']) as Date } : {}),
    ...(d['preferredLanguage'] ? { preferredLanguage: d['preferredLanguage'] } : {}),
    ...(typeof d['isFavorite'] === 'boolean' ? { isFavorite: d['isFavorite'] as boolean } : {})
  } as ServiceProvider;
}

@Injectable({
  providedIn: 'root'
})
export class ServiceProvidersService implements OnDestroy {
  private serviceProvidersData: ServiceProvider[] = [];
  private serviceProvidersSubject = new BehaviorSubject<ServiceProvider[]>([]);
  public serviceProviders$ = this.serviceProvidersSubject.asObservable();
  private unsubscribe?: () => void;
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly ngZone = inject(NgZone);

  constructor(
    private firestore: Firestore,
  ) {
    this.initializeService();
  }

  private initializeService(): void {
    // React to workspace signal changes
    effect(() => {
      const workspace = this.workspaceStore.currentWorkspace();
      if (workspace) {
        this.setupServiceProvidersListener(workspace.id);
      } else {
        this.clearData();
      }
    });
  }

  private setupServiceProvidersListener(workspaceId: string): void {

    // Clean up existing listener
    if (this.unsubscribe) {
      this.unsubscribe();
    }

    const serviceProvidersCollection = collection(
      this.firestore,
      `workspaces/${workspaceId}/service_providers`
    );

    const serviceProvidersQuery = query(
      serviceProvidersCollection,
      orderBy('name', 'asc')
    );

    this.unsubscribe = onSnapshot(
      serviceProvidersQuery,
      (snapshot) => {

        const serviceProviders: ServiceProvider[] = snapshot.docs.map(
          doc => mapServiceProviderDoc(doc.id, doc.data()),
        );

        this.serviceProvidersData = serviceProviders;
        // `@angular/fire` runs the onSnapshot callback outside the Angular
        // zone, so emit inside it — otherwise OnPush consumers (e.g. the
        // service-providers list) don't get a change-detection tick and the
        // first async load never renders until an unrelated in-zone event.
        this.ngZone.run(() => this.serviceProvidersSubject.next(serviceProviders));
      },
      (error) => {
        // Silence permission-denied during auth transitions (logout/login).
        // See utils/auth-transition-error.ts.
        if (!isPermissionDeniedError(error)) {
          console.error('ServiceProvidersService: Error in service providers listener:', error);
        }
        // In case of error, still emit empty array to prevent UI issues
        this.ngZone.run(() => this.serviceProvidersSubject.next([]));
      }
    );
  }

  private clearData(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = undefined;
    }
    this.serviceProvidersData = [];
    this.serviceProvidersSubject.next([]);
  }

  private getCurrentWorkspaceId(): string {
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) {
      throw new Error('No active workspace found');
    }
    return workspaceId;
  }

  // Get all service providers
  getAllServiceProviders(): Observable<ServiceProvider[]> {
    return this.serviceProviders$;
  }

  /**
   * Get service provider by UID from cache stream (continuous)
   * This method properly waits for data to be available, handling F5 refresh scenarios
   * @param providerUID The provider UID to find
   * @returns Observable that emits when the provider is found in cache
   */
  getServiceProviderById$(providerUID: string): Observable<ServiceProvider | null> {
    return this.serviceProviders$.pipe(
      map(providers => providers.find(p => p.providerUID === providerUID) || null)
    );
  }

  // Get service provider by UID
  getServiceProviderByUID(providerUID: string): Observable<ServiceProvider | null> {
    console.log('🔍 ServiceProvidersService: getServiceProviderByUID called with UID:', providerUID);

    try {
      const workspaceId = this.getCurrentWorkspaceId();
      console.log('🔍 ServiceProvidersService: Current workspace ID:', workspaceId);

      const docPath = `workspaces/${workspaceId}/service_providers/${providerUID}`;
      console.log('🔍 ServiceProvidersService: Document path:', docPath);

      const providerDocRef = doc(this.firestore, docPath);

      return from(getDoc(providerDocRef)).pipe(
        map(docSnap => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            const serviceProvider = {
              providerUID: docSnap.id,
              name: data['name'],
              type: data['type'],
              contactInfo: data['contactInfo'],
              rating: data['rating'],
              isActive: data['isActive'],
              specialties: data['specialties'],
              notes: data['notes'],
              createdAt: toDate(data['createdAt']) ?? new Date(),
              updatedAt: toDate(data['updatedAt']) ?? new Date(),

              // Enhanced fields
              propertyPreferences: data['propertyPreferences'] ? {
                preferredPropertyTypes: data['propertyPreferences'].preferredPropertyTypes || [],
                maximumProperties: data['propertyPreferences'].maximumProperties,
                preferredLocations: data['propertyPreferences'].preferredLocations || [],
                excludedLocations: data['propertyPreferences'].excludedLocations || [],
                minimumJobValue: data['propertyPreferences'].minimumJobValue,
                notes: data['propertyPreferences'].notes
              } : undefined,

              communicationSettings: data['communicationSettings'] ? {
                preferredMethod: data['communicationSettings'].preferredMethod,
                emergencyContact: data['communicationSettings'].emergencyContact,
                responseTimeExpected: data['communicationSettings'].responseTimeExpected,
                workingHours: data['communicationSettings'].workingHours,
                availableDays: data['communicationSettings'].availableDays
              } : undefined,

              performanceMetrics: data['performanceMetrics'] ? {
                completedJobs: data['performanceMetrics'].completedJobs || 0,
                averageRating: data['performanceMetrics'].averageRating || 0,
                onTimeCompletionRate: data['performanceMetrics'].onTimeCompletionRate || 0,
                responseTimeAverage: data['performanceMetrics'].responseTimeAverage || 0,
                customerSatisfactionScore: data['performanceMetrics'].customerSatisfactionScore || 0,
                repeatCustomerRate: data['performanceMetrics'].repeatCustomerRate || 0,
                lastPerformanceUpdate: toDate(data['performanceMetrics'].lastPerformanceUpdate) ?? new Date()
              } : undefined,

              availabilitySchedule: data['availabilitySchedule'] ? {
                availability: data['availabilitySchedule'].availability || [],
                emergencyAvailable: data['availabilitySchedule'].emergencyAvailable || false,
                leadTimeRequired: data['availabilitySchedule'].leadTimeRequired || 0,
                maxJobsPerWeek: data['availabilitySchedule'].maxJobsPerWeek,
                currentlyAvailable: data['availabilitySchedule'].currentlyAvailable !== false,
                nextAvailableDate: toDate(data['availabilitySchedule'].nextAvailableDate) ?? undefined,
                unavailablePeriods: data['availabilitySchedule'].unavailablePeriods?.map((period: { start?: { toDate: () => Date }; end?: { toDate: () => Date }; reason?: string }) => ({
                  start: toDate(period.start) ?? undefined,
                  end: toDate(period.end) ?? undefined,
                  reason: period.reason
                })) || []
              } : undefined,

              businessInfo: data['businessInfo'] ? {
                companyName: data['businessInfo'].companyName,
                licenseNumber: data['businessInfo'].licenseNumber,
                insuranceDetails: data['businessInfo'].insuranceDetails,
                website: data['businessInfo'].website,
                yearsInBusiness: data['businessInfo'].yearsInBusiness,
                certifications: data['businessInfo'].certifications || []
              } : undefined,

              bankAccount: data['bankAccount'] ? {
                iban: data['bankAccount'].iban || '',
                bic: data['bankAccount'].bic || '',
                accountHolder: data['bankAccount'].accountHolder || ''
              } : undefined,

              ...(data['logoUrl'] ? { logoUrl: data['logoUrl'] as string } : {}),

              // Claim state (#218). This mapping copies field by field, so
              // anything not listed is silently dropped — and `claimStatus`
              // was. Every caller of this loader therefore saw an unclaimed
              // provider whatever the document said, which is why the edit
              // route could not arm its lock even on a claimed profile. Caught
              // by the browser check, not by a spec: the specs fed the
              // component a provider object directly and never went through
              // here.
              claimStatus: data['claimStatus'],
              claimedByWorkspaceId: data['claimedByWorkspaceId'],
              claimedAt: toDate(data['claimedAt']) ?? undefined,
              invitationSentAt: toDate(data['invitationSentAt']) ?? undefined,
              invitationId: data['invitationId'],
              isMasterProfile: data['isMasterProfile']
            } as ServiceProvider;

            return serviceProvider;
          } else {
            console.warn('⚠️ ServiceProvidersService: Document does not exist for UID:', providerUID);
            return null;
          }
        }),
        catchError(error => {
          console.error('❌ ServiceProvidersService: Error in getServiceProviderByUID:', error);
          return of(null);
        })
      );
    } catch (error) {
      console.error('❌ ServiceProvidersService: Error getting provider:', error);
      return of(null);
    }
  }

  // Get service providers by type
  getServiceProvidersByType(type: ServiceProviderType): Observable<ServiceProvider[]> {
    return this.serviceProviders$.pipe(
      map((providers: ServiceProvider[]) => providers.filter((provider: ServiceProvider) => provider.type === type))
    );
  }

  // Add new service provider
  addServiceProvider(provider: Omit<ServiceProvider, 'providerUID' | 'createdAt' | 'updatedAt'>): Observable<ServiceProvider> {
    try {
      const workspaceId = this.getCurrentWorkspaceId();

      const serviceProvidersCollection = collection(this.firestore, `workspaces/${workspaceId}/service_providers`);

      const newProviderData = {
        name: provider.name,
        type: provider.type,
        contactInfo: provider.contactInfo,
        rating: provider.rating,
        isActive: provider.isActive,
        specialties: provider.specialties,
        notes: provider.notes,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),

        // Enhanced fields - only include if they exist
        ...(provider.propertyPreferences && {
          propertyPreferences: {
            preferredPropertyTypes: provider.propertyPreferences.preferredPropertyTypes || [],
            ...(provider.propertyPreferences.maximumProperties !== null && provider.propertyPreferences.maximumProperties !== undefined && {
              maximumProperties: provider.propertyPreferences.maximumProperties
            }),
            preferredLocations: provider.propertyPreferences.preferredLocations || [],
            excludedLocations: provider.propertyPreferences.excludedLocations || [],
            ...(provider.propertyPreferences.minimumJobValue !== null && provider.propertyPreferences.minimumJobValue !== undefined && {
              minimumJobValue: provider.propertyPreferences.minimumJobValue
            }),
            ...(provider.propertyPreferences.notes && {
              notes: provider.propertyPreferences.notes
            })
          }
        }),

        ...(provider.communicationSettings && {
          communicationSettings: {
            preferredMethod: provider.communicationSettings.preferredMethod,
            ...(provider.communicationSettings.emergencyContact && {
              emergencyContact: provider.communicationSettings.emergencyContact
            }),
            responseTimeExpected: provider.communicationSettings.responseTimeExpected,
            workingHours: provider.communicationSettings.workingHours,
            availableDays: provider.communicationSettings.availableDays
          }
        }),

        ...(provider.performanceMetrics && {
          performanceMetrics: {
            completedJobs: provider.performanceMetrics.completedJobs || 0,
            averageRating: provider.performanceMetrics.averageRating || 0,
            onTimeCompletionRate: provider.performanceMetrics.onTimeCompletionRate || 0,
            responseTimeAverage: provider.performanceMetrics.responseTimeAverage || 0,
            customerSatisfactionScore: provider.performanceMetrics.customerSatisfactionScore || 0,
            repeatCustomerRate: provider.performanceMetrics.repeatCustomerRate || 0,
            lastPerformanceUpdate: Timestamp.fromDate(provider.performanceMetrics.lastPerformanceUpdate || new Date())
          }
        }),

        ...(provider.availabilitySchedule && {
          availabilitySchedule: {
            availability: provider.availabilitySchedule.availability || [],
            emergencyAvailable: provider.availabilitySchedule.emergencyAvailable || false,
            leadTimeRequired: provider.availabilitySchedule.leadTimeRequired || 0,
            ...(provider.availabilitySchedule.maxJobsPerWeek !== null && provider.availabilitySchedule.maxJobsPerWeek !== undefined && {
              maxJobsPerWeek: provider.availabilitySchedule.maxJobsPerWeek
            }),
            currentlyAvailable: provider.availabilitySchedule.currentlyAvailable !== false,
            ...(provider.availabilitySchedule.nextAvailableDate && {
              nextAvailableDate: Timestamp.fromDate(provider.availabilitySchedule.nextAvailableDate)
            }),
            unavailablePeriods: provider.availabilitySchedule.unavailablePeriods?.map(period => ({
              start: Timestamp.fromDate(period.start),
              end: Timestamp.fromDate(period.end),
              reason: period.reason
            })) || []
          }
        }),

        ...(provider.businessInfo && {
          businessInfo: {
            ...(provider.businessInfo.companyName && {
              companyName: provider.businessInfo.companyName
            }),
            ...(provider.businessInfo.licenseNumber && {
              licenseNumber: provider.businessInfo.licenseNumber
            }),
            ...(provider.businessInfo.insuranceDetails && {
              insuranceDetails: provider.businessInfo.insuranceDetails
            }),
            ...(provider.businessInfo.website && {
              website: provider.businessInfo.website
            }),
            ...(provider.businessInfo.yearsInBusiness !== null && provider.businessInfo.yearsInBusiness !== undefined && {
              yearsInBusiness: provider.businessInfo.yearsInBusiness
            }),
            certifications: provider.businessInfo.certifications || []
          }
        }),

        // Only persist a bank-account map when at least one field is filled, and
        // coerce each field to a string so Firestore never sees `undefined`
        // (the frontend SDK has no `ignoreUndefinedProperties`).
        ...(provider.bankAccount &&
          (provider.bankAccount.iban || provider.bankAccount.bic || provider.bankAccount.accountHolder) && {
          bankAccount: {
            iban: provider.bankAccount.iban || '',
            bic: provider.bankAccount.bic || '',
            accountHolder: provider.bankAccount.accountHolder || ''
          }
        }),

        ...(provider.logoUrl ? { logoUrl: provider.logoUrl } : {})
      };

      return from(addDoc(serviceProvidersCollection, newProviderData)).pipe(
        map(docRef => {
          return {
            ...provider,
            providerUID: docRef.id,
            createdAt: new Date(),
            updatedAt: new Date()
          } as ServiceProvider;
        }),
        catchError(error => {
          console.error('ServiceProvidersService: Error creating service provider:', error);

          let errorMessage = 'Failed to create service provider. Please try again.';

          if (error?.code === 'permission-denied') {
            errorMessage = 'You do not have permission to create particular service providers in this workspace.';
          } else if (error?.code === 'invalid-argument') {
            errorMessage = 'Invalid data provided. Please check all required fields and try again.';
          }

          return throwError(() => new Error(errorMessage));
        })
      );
    } catch (error) {
      console.error('ServiceProvidersService: Error:', error);
      return throwError(() => new Error('Workspace must be selected to create service provider'));
    }
  }

  /**
   * Update a provider in the owner's carnet.
   *
   * Central guard for #218: once the provider has claimed their profile, the
   * fields listed in `PROVIDER_MASTER_FIELDS` belong to them, and
   * `syncProfileToLinkedOwners` pushes their version back over anything written
   * here. The UI already refuses the edit on the detail screen, but every write
   * path funnels through this method — the store, the edit route, any future
   * caller — so this is where the rule actually holds.
   *
   * The Firestore rules refuse the same write server-side; this exists to say
   * WHY, since a rules rejection reaches the client as an opaque
   * permission-denied.
   */
  updateServiceProvider(providerUID: string, updates: Partial<ServiceProvider>): Observable<ServiceProvider | null> {
    try {
      const workspaceId = this.getCurrentWorkspaceId();

      const providerDocRef = doc(this.firestore, `workspaces/${workspaceId}/service_providers/${providerUID}`);

      const updateData = buildProviderUpdatePayload(updates);
      const masterFields = masterFieldsTouchedBy(updateData);

      if (masterFields.length) {
        return from(getDoc(providerDocRef)).pipe(
          switchMap((snapshot) => {
            const current = snapshot.data() as ServiceProvider | undefined;
            if (isClaimedProfile(current)) {
              return throwError(
                () =>
                  new ProviderProfileLockedError(
                    `Provider ${providerUID} has claimed their profile; ` +
                      `${masterFields.join(', ')} is theirs to change.`,
                  ),
              );
            }
            return this.writeProviderUpdate(providerDocRef, updateData, providerUID);
          }),
        );
      }

      return this.writeProviderUpdate(providerDocRef, updateData, providerUID);
    } catch (error) {
      console.error('ServiceProvidersService: Error:', error);
      return throwError(() => new Error('Workspace required to update service provider'));
    }
  }

  private writeProviderUpdate(
    providerDocRef: ReturnType<typeof doc>,
    updateData: ProviderUpdatePayload,
    providerUID: string,
  ): Observable<ServiceProvider | null> {
    return from(updateDoc(providerDocRef, updateData)).pipe(
      switchMap(() => this.getServiceProviderByUID(providerUID)),
      tap(() => console.log('ServiceProvidersService: Service provider updated successfully')),
      catchError(error => {
        console.error('ServiceProvidersService: Error updating service provider:', error);
        return throwError(() => new Error('Failed to update service provider. Please try again.'));
      })
    );
  }

  // Delete service provider
  deleteServiceProvider(providerUID: string): Observable<boolean> {
    try {
      const workspaceId = this.getCurrentWorkspaceId();

      const providerDocRef = doc(this.firestore, `workspaces/${workspaceId}/service_providers/${providerUID}`);

      return from(deleteDoc(providerDocRef)).pipe(
        map(() => {
          return true;
        }),
        catchError(error => {
          console.error('ServiceProvidersService: Error deleting service provider:', error);
          return throwError(() => new Error('Failed to delete service provider. Please try again.'));
        })
      );
    } catch (error) {
      console.error('ServiceProvidersService: Error:', error);
      return throwError(() => new Error('Workspace required to delete service provider'));
    }
  }

  ngOnDestroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.serviceProvidersSubject.complete();
  }
} 